import { Principal } from '@icp-sdk/core/principal';
import type { IDL } from '@icp-sdk/core/candid';
import { HOST } from './api';
import { traderResult } from './traders';
import type { WalletChallenge } from './premium';
import { analyticsAccountIdl, type AnalyticsAccountService } from './site-analytics';

type LinkActor = {
  confirm_premium_wallet_link(owner: Principal, id: bigint): Promise<{ ok: null } | { err: string }>;
};
type Plug = {
  requestConnect(options: { whitelist: string[]; host: string; timeout: number }): Promise<unknown>;
  agent?: { getPrincipal(): Promise<Principal> };
  createActor<T>(options: { canisterId: string; interfaceFactory: IDL.InterfaceFactory }): Promise<T>;
};

// Restrict the wallet's interfaces to ownership and account observations. No token ledger,
// allowance, transfer or trading methods are requested from Plug.
const proofIdl: IDL.InterfaceFactory = ({ IDL }) => IDL.Service({
  confirm_premium_wallet_link: IDL.Func([IDL.Principal, IDL.Nat64], [IDL.Variant({ ok: IDL.Null, err: IDL.Text })], []),
});

export async function linkPremiumPlugWallet(owner: string, challenge: (wallet: Principal) => Promise<WalletChallenge>, assertCurrent: () => void) {
  const plug = (window as Window & { ic?: { plug?: Plug } }).ic?.plug;
  if (!plug) throw Error('Install or enable the Plug browser extension, then try again.');
  const canisterId = import.meta.env.VITE_TRADERS_CANISTER_ID;
  if (!canisterId) throw Error('The trader canister is not configured for this deployment.');
  // This is the first asynchronous call, directly from the user's button click.
  const connected = await plug.requestConnect({ whitelist: [canisterId, import.meta.env.VITE_BACKEND_CANISTER_ID].filter(Boolean), host: import.meta.env.VITE_DFX_NETWORK === 'local' ? import.meta.env.VITE_DFX_HOST : HOST, timeout: 50_000 });
  if (connected === false) throw Error('Plug request was cancelled.');
  assertCurrent();
  if (!plug.agent) throw Error('Plug did not provide a wallet account. Unlock Plug and try again.');
  const wallet = Principal.fromText((await plug.agent.getPrincipal()).toText());
  if (wallet.isAnonymous()) throw Error('Choose a signed Plug account to prove ownership.');
  const pending = await challenge(wallet);
  assertCurrent();
  if (pending.owner.toText() !== owner || pending.wallet.toText() !== wallet.toText()) throw Error('The wallet challenge changed. Start wallet verification again.');
  if ((await plug.agent.getPrincipal()).toText() !== wallet.toText()) throw Error('Your Plug account changed. Start wallet verification again.');
  const actor = await plug.createActor<LinkActor>({ canisterId, interfaceFactory: proofIdl });
  assertCurrent();
  traderResult(await actor.confirm_premium_wallet_link(Principal.fromText(owner), pending.id));
  assertCurrent();
  const analyticsId = import.meta.env.VITE_BACKEND_CANISTER_ID;
  if (analyticsId) void plug.createActor<AnalyticsAccountService>({ canisterId: analyticsId, interfaceFactory: analyticsAccountIdl }).then(service => service.analytics_account_seen({ plug: null })).then(result => { if ('ok' in result) window.dispatchEvent(new Event('soonscreener:analytics-updated')); }).catch(() => undefined);
  return wallet.toText();
}
